import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "./cn";

// Surface primitives — v1's card/panel vocabulary, which v2 had no equivalent of at all:
// every screen rendered bare content directly onto the page background, which is most of why
// it read as a wireframe. A panel is a bordered surface at 12px radius; a section is a panel
// with an eyebrow title; the eyebrow itself is v1's signature micro-label.

export function Eyebrow({ className, accent, ...props }: React.HTMLAttributes<HTMLDivElement> & { accent?: boolean }) {
  return <div className={cn("eyebrow", accent && "text-accent2", className)} {...props} />;
}

export function Panel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-card border border-line bg-surface", className)}
      {...props}
    />
  );
}

/** Section — a titled panel. The title is an eyebrow, per v1, not an <h3> in body type.
 *
 * Optionally collapsible, and REMEMBERS the choice: these pages stack many panels and a
 * console that re-expands everything on every visit makes the operator re-close it every
 * time. `id` is the persistence key — without one, collapse still works but is not remembered.
 */
export function Section({
  title,
  actions,
  children,
  bodyClassName,
  className,
  collapsible,
  defaultOpen = true,
  id,
  hint,
}: {
  title: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  id?: string;
  /** Shown next to the title while collapsed, so a closed panel still says what is inside. */
  hint?: React.ReactNode;
}) {
  const key = id ? `ozpanel.section.${id}` : null;
  const [open, setOpen] = React.useState(() => {
    if (!collapsible) return true;
    if (key) {
      try {
        const v = localStorage.getItem(key);
        if (v === "0") return false;
        if (v === "1") return true;
      } catch { /* storage disabled — fall through to the default */ }
    }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (key) {
        try { localStorage.setItem(key, next ? "1" : "0"); } catch { /* not fatal */ }
      }
      return next;
    });
  };

  const heading = (
    <>
      {collapsible && (
        <ChevronRight size={12} className={cn("shrink-0 text-faint transition-transform", open && "rotate-90")} />
      )}
      <Eyebrow accent>{title}</Eyebrow>
      {hint && !open && <span className="truncate text-[11.5px] text-faint">{hint}</span>}
    </>
  );

  return (
    <Panel className={cn("overflow-hidden", className)}>
      {/* Wraps: with two or more actions the title used to shrink to one word per line on a phone.
          Now the actions drop below the title when they do not fit beside it. */}
      <div className={cn("flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-4 py-3", open ? "border-line" : "border-transparent")}>
        {collapsible ? (
          <button type="button" onClick={toggle} aria-expanded={open} className="flex min-h-8 min-w-[min(100%,13rem)] flex-1 items-center gap-2 text-left pointer-coarse:min-h-10">
            {heading}
          </button>
        ) : (
          <div className="flex min-w-[min(100%,13rem)] flex-1 items-center gap-2">{heading}</div>
        )}
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {open && <div className={cn("p-4", bodyClassName)}>{children}</div>}
    </Panel>
  );
}

/** Stat — v1's tile: mono eyebrow key over a large tight-tracked value. */
export function Stat({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "ok" | "warn" | "crit" | "accent";
  icon?: React.ReactNode;
}) {
  const toneClass =
    tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "crit" ? "text-crit" : tone === "accent" ? "text-accent" : "text-fg";
  return (
    <Panel className="px-[18px] py-4">
      <div className="flex items-start justify-between gap-2">
        <Eyebrow>{label}</Eyebrow>
        {icon && <span className="text-faint">{icon}</span>}
      </div>
      <div className={cn("mt-1.5 text-[26px] leading-tight font-[680] tracking-[-0.02em] tabular", toneClass)}>
        {value}
      </div>
      {hint && <div className="mt-1 text-[12px] text-muted">{hint}</div>}
    </Panel>
  );
}

/** Meter — v1's `.rest` bar, for anything measured against a ceiling. */
export function Meter({ pct, tone }: { pct: number; tone?: "ok" | "warn" | "crit" }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const bar = tone === "crit" ? "bg-crit" : tone === "warn" ? "bg-warn" : "bg-accent";
  return (
    <div className="h-[7px] overflow-hidden rounded-[4px] bg-line2">
      <i className={cn("block h-full rounded-[4px] transition-[width] duration-500", bar)} style={{ width: `${clamped}%` }} />
    </div>
  );
}
